/**
 * Pure function for the campaign rebuild with oven splitting (replanning + fresh planning).
 * Extracted from ProductionScheduleWizardContext to make it testable.
 *
 * Strategy:
 *  1. Recompute the oven batches per campaign from that campaign's own dough quantity.
 *  2. If oven batches > availableOvens: split into sub-campaigns.
 *     Number of splits = min(ceil(ovenCount / availableOvens), free sequence entries).
 *  3. If a campaign contains fixed dough batches (frozen zone, recognised by UUID):
 *     split 1 = fixed + new up to the capacity; splits 2+ = the remaining new production.
 *  4. No sequence entries -> unlimited splits (standard fresh planning).
 */

import {
  calculateBatchDetermination,
  type DoughBatchInput,
  type MaterialBatchParams,
} from './batchDeterminationService';
import type { Campaign, DoughBatch, OvenBatch } from '../contexts/ProductionScheduleWizardContext';

export interface RebuildCampaignsParams {
  campaigns: Campaign[];
  /** UUIDs of the dough batches inside the frozen zone window (from getFixedDoughBatchesUntil). */
  fixedDoughBatchUuids: Set<string>;
  /** Sequence entries of the product (sorted by sequenceNumber). */
  productSequenceEntries: ReadonlyArray<{ sequenceNumber?: number; maxBatches?: number | null }>;
  availableOvens: number;
  materialParams: MaterialBatchParams;
  batchDetermination: 'FULL_OVEN' | 'OVEN_UTILIZATION';
  minOvenUtilizationPercent: number;
  doughBatchMethod: 'even' | 'full';
  /** Returns a unique campaign id; the parameter is the running index (1-based). */
  generateCampaignId: (builtCount: number) => string;
}

/**
 * Rebuilds the campaign list with correct per-campaign oven batches and
 * greedy oven splitting, limited by the available sequence entries.
 */
export function rebuildCampaignsWithSplitting(params: RebuildCampaignsParams): Campaign[] {
  const {
    campaigns,
    fixedDoughBatchUuids,
    productSequenceEntries,
    availableOvens,
    materialParams,
    batchDetermination,
    minOvenUtilizationPercent,
    doughBatchMethod,
    generateCampaignId,
  } = params;

  const capacity = availableOvens * materialParams.piecesPerOven;
  const seqEntryCount = productSequenceEntries.length;
  const rebuilt: Campaign[] = [];
  let nextEntryIndex = 0;

  const seqNumberAt = (idx: number, fallback: number): number =>
    seqEntryCount > 0 && idx < seqEntryCount
      ? (productSequenceEntries[idx].sequenceNumber ?? fallback)
      : fallback;

  const commonCalcParams = {
    batchDetermination,
    minOvenUtilizationPercent,
    doughBatchMethod,
    material: materialParams,
  };

  for (const original of campaigns) {
    const campaignPieces = original.doughBatches.reduce((s, db) => s + (db.quantity ?? 0), 0);
    if (campaignPieces <= 0) {
      rebuilt.push(original);
      continue;
    }

    // Separate the fixed and the new dough batches of this campaign
    const campaignFixed: DoughBatch[] = original.doughBatches.filter(
      db => db.uuid != null && fixedDoughBatchUuids.has(db.uuid),
    );
    const fixedPieces = campaignFixed.reduce((s, db) => s + (db.quantity ?? 0), 0);

    // Reindex fixed batches to 1, 2, 3, ... per call. validateFixedDoughBatches in
    // batchDeterminationService rejects gaps and offsets - if the fixed batches in this
    // campaign have sequenceNumber 4,5,6 (because earlier campaigns of the same material
    // hold 1,2,3), forwarding the raw values throws "Erwartet 1, erhalten 4".
    // The original sequenceNumber on the DoughBatch object is left intact for callers
    // that need it; only the input to the service is renumbered.
    const toFixedInput = (db: DoughBatch, idx: number): DoughBatchInput => ({
      sequenceNumber: idx + 1,
      quantity: db.quantity,
      doughQuantityKg: db.doughQuantityKg,
      uuid: db.uuid,
    });

    // Compute the oven batches for the total production of this campaign
    const fullResult = calculateBatchDetermination({
      desiredQuantity: campaignPieces,
      ...commonCalcParams,
      fixedDoughBatches: campaignFixed.length > 0 ? campaignFixed.map(toFixedInput) : undefined,
    });

    const ovenCount = fullResult.ovenBatches.length;

    // How many sequence slots are still available?
    // - seqEntryCount === 0 -> unlimited splits (fresh planning fallback, no sequence given)
    // - seqEntryCount > 0   -> bounded by remaining sequence entries; the LAST slot takes
    //   whatever is left (a slot's maxBatches limits the dough-batch count INSIDE the
    //   slot, not the number of slots we're allowed to consume).
    const slotsLeft: number =
      seqEntryCount === 0
        ? Infinity
        : Math.max(1, seqEntryCount - nextEntryIndex);
    const requiredSplits = Math.ceil(ovenCount / availableOvens);
    const actualSplits =
      ovenCount <= availableOvens
        ? 1
        : slotsLeft === Infinity
          ? requiredSplits
          : Math.min(requiredSplits, slotsLeft);

    if (actualSplits <= 1) {
      rebuilt.push({
        ...original,
        sequenceNumber: seqNumberAt(nextEntryIndex, original.sequenceNumber),
        campaignId: generateCampaignId(rebuilt.length + 1),
        totalBakedQuantity: fullResult.bakedQuantity,
        doughBatches: fullResult.doughBatches as DoughBatch[],
        ovenBatches: fullResult.ovenBatches as OvenBatch[],
      });
      nextEntryIndex++;
      continue;
    }

    if (campaignFixed.length > 0) {
      // Mixed campaign (fixed + new batches):
      // split 1: fixed batches + new production up to the oven capacity
      const split1Desired = Math.max(fixedPieces, Math.min(campaignPieces, capacity));
      const split1 = calculateBatchDetermination({
        desiredQuantity: split1Desired,
        ...commonCalcParams,
        fixedDoughBatches: campaignFixed.map(toFixedInput),
      });
      rebuilt.push({
        campaignId: generateCampaignId(rebuilt.length + 1),
        sequenceNumber: seqNumberAt(nextEntryIndex, original.sequenceNumber),
        materialId: original.materialId,
        materialName: original.materialName,
        desiredQuantity: original.desiredQuantity,
        totalBakedQuantity: split1.bakedQuantity,
        dueDate: original.dueDate,
        doughBatches: split1.doughBatches as DoughBatch[],
        ovenBatches: split1.ovenBatches as OvenBatch[],
      });
      nextEntryIndex++;

      // Splits 2+: the remaining new production (without fixed batches)
      let remaining = fullResult.bakedQuantity - split1.bakedQuantity;
      for (let i = 1; i < actualSplits && remaining > 0; i++) {
        const isLast = i === actualSplits - 1;
        const qty = isLast ? remaining : Math.min(capacity, remaining);
        remaining -= qty;
        const spl = calculateBatchDetermination({ desiredQuantity: qty, ...commonCalcParams });
        rebuilt.push({
          campaignId: generateCampaignId(rebuilt.length + 1),
          sequenceNumber: seqNumberAt(nextEntryIndex, original.sequenceNumber),
          materialId: original.materialId,
          materialName: original.materialName,
          desiredQuantity: 0,
          totalBakedQuantity: spl.bakedQuantity,
          dueDate: original.dueDate,
          doughBatches: spl.doughBatches as DoughBatch[],
          ovenBatches: spl.ovenBatches as OvenBatch[],
        });
        nextEntryIndex++;
      }
    } else {
      // Purely new campaign: greedy split at the oven capacity
      let remaining = fullResult.bakedQuantity;
      for (let i = 0; i < actualSplits; i++) {
        const isLast = i === actualSplits - 1;
        const qty = isLast ? remaining : Math.min(capacity, remaining);
        remaining -= qty;
        const spl = calculateBatchDetermination({ desiredQuantity: qty, ...commonCalcParams });
        rebuilt.push({
          campaignId: generateCampaignId(rebuilt.length + 1),
          sequenceNumber: seqNumberAt(nextEntryIndex, original.sequenceNumber),
          materialId: original.materialId,
          materialName: original.materialName,
          desiredQuantity: i === 0 ? original.desiredQuantity : 0,
          totalBakedQuantity: spl.bakedQuantity,
          dueDate: original.dueDate,
          doughBatches: spl.doughBatches as DoughBatch[],
          ovenBatches: spl.ovenBatches as OvenBatch[],
        });
        nextEntryIndex++;
      }
    }
  }

  return rebuilt;
}
